import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
  TABLE_HEADER_CELL_CLASS,
  useModalRequestClose,
} from '@safetyhub/ui';
import {
  LEGAL_OBLIGATION_AGENCY_FIELD_LABEL,
  LEGAL_OBLIGATION_APPLICABILITY_SCOPE_LABEL,
  LEGAL_OBLIGATION_CONDITION_AXIS_LABEL,
  LEGAL_OBLIGATION_CONDITION_SOURCE_KIND_LABEL,
  LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL,
  LEGAL_OBLIGATION_EVIDENCE_SOURCE_LABEL,
  LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL,
  LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL,
  LEGAL_OBLIGATION_PERIOD_BASIS_LABEL,
  LEGAL_OBLIGATION_RECURRENCE_LABEL,
  LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL,
  LEGAL_POSITIONS,
  LEGAL_POSITION_ORDER,
  OBLIGATION_RECURRENCE_VALUES,
  formatAnnexLabel,
  formatDateTimeISO,
  formatObligationInterval,
  formatObligationRecurrence,
  type AdminLegalObligationConditionSourceSnapshot,
  type AdminLegalObligationRow,
  type AdminLegalObligationTimelineEntry,
  type LegalPositionKey,
} from '@safetyhub/shared';
import { extractApiError } from '../lib/api';
import {
  LEGAL_OBLIGATION_CATALOG_QUERY_KEYS,
  legalObligationCatalogApi,
} from '../lib/legalObligationCatalog';
import {
  CATALOG_MISSING_FIELD_LABEL,
  blocksMaterialization,
  buildUpdateBody,
  anchorSourceCandidates,
  eventChoiceVariantsOf,
  eventSourceCandidates,
  formBlockedReason,
  formToRule,
  missingCatalogFields,
  revisionFieldLabel,
  rowToForm,
  subjectModeOf,
  type CatalogFormState,
} from '../lib/legalObligationCatalogFields';
import { useToastStore } from '../stores/toast';
import UpcomingAmendmentsPanel from '../components/legal-obligations/UpcomingAmendmentsPanel';
import ObligationTemplateLinksModal from '../components/legal-obligations/ObligationTemplateLinksModal';
import CatalogConditionsModal from '../components/legal-obligations/CatalogConditionsModal';

/**
 * **법정 의무 카탈로그** — 플랫폼이 한 벌 관리하는 의무 정의를 고치는 화면(사용자 결정 2026-10-29 「화면 먼저」).
 *
 * ## 왜 이 화면이 필요했나
 * 활성 의무 37개가 제목만 있고 주기·기간 기준·기한 기준·담당 주체가 비어 있었다(seed가 채우지 않는다 —
 * 그 값은 조문 검증의 산출물이다 · 모델 docblock). 주기가 비면 실체화가 `RECURRENCE_UNSET`으로 skip되어
 * **어느 현장에도 인스턴스가 생기지 않는다.** 고칠 서버 API(`PATCH`)는 있었고 화면이 없었다.
 *
 * ## 쓰기 입구 셋(카탈로그 쓰기 경로 하나 설계 W3 2026-11-04 — `docs/plans/2026-11-03-legal-catalog-single-write-path-design.md` §6)
 * - **편집** 모달 — 의무 칸(대상·사건 공유 원천 넷 포함)을 모아 한 번 저장 = 서버 `updateObligation()` = **이력 1행 + version +1**
 *   (달라진 칸이 없으면 no-op). 「조문과 대조 완료」 버튼은 저장과 별개로 즉시 기록된다(개정 아님 · audit).
 * - **조건·변형** 모달(`CatalogConditionsModal`) — 줄 하나 저장마다 서버가 따로 기록한다(규칙 값 = 개정 · 근거·대조 = 별 기록).
 * - **이력** 모달 — 서버 `GET :id/timeline`(개정 + 별 기록을 시간순 한 줄기 · 종류 표시).
 *
 * ## 이 화면이 «하지 않는» 것
 * - `key`·`displayOrder`·`smsEscalation`을 고치지 않는다 — PATCH DTO에 칸이 없다(그 셋을 여는 판정은 DTO docblock의 ⓐⓑⓒ다). 값은 보여 준다.
 *   `lastVerifiedAt`은 칸이 아니라 「대조 완료」 행위로만 바뀐다(서버 시각).
 * - 개정 «되돌리기»를 주지 않는다 — 서버 docblock이 그 UX를 정하지 않았다.
 */
export default function LegalObligationCatalog() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminLegalObligationRow | null>(null);
  const [historyOf, setHistoryOf] = useState<AdminLegalObligationRow | null>(null);
  // L24 — 의무 ↔ 양식 링크 저작(카탈로그 개정과 다른 축이라 이력·version을 건드리지 않는다)
  const [templatesOf, setTemplatesOf] = useState<AdminLegalObligationRow | null>(null);
  // W3 — 조건·변형 줄 편집. ⚠️ id로 들고 행은 «최신 목록»에서 찾는다 — 줄 쓰기마다 목록을 다시 읽어 version·줄이 바뀌므로 스냅샷을 들면 낡는다
  const [linesOfId, setLinesOfId] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const list = useQuery({
    queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.list,
    queryFn: () => legalObligationCatalogApi.list(),
  });

  const rows = useMemo(() => list.data ?? [], [list.data]);
  const linesOf = linesOfId === null ? null : (rows.find((r) => r.id === linesOfId) ?? null);
  const refresh = (obligationId: string) => {
    queryClient.invalidateQueries({ queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.list });
    queryClient.invalidateQueries({ queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.timeline(obligationId) });
  };
  const activeRows = rows.filter((r) => r.isActive);
  const blockedCount = activeRows.filter(blocksMaterialization).length;
  const missingCount = activeRows.filter((r) => missingCatalogFields(r).length > 0).length;
  const visibleRows = onlyMissing ? rows.filter((r) => missingCatalogFields(r).length > 0) : rows;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">법정 의무 카탈로그</h1>
        <p className="mt-1 text-sm text-gray-500">
          모든 현장에 공통으로 적용되는 법정 의무의 정의입니다. 주기가 비어 있는 의무는 현장에 이행
          항목이 만들어지지 않습니다.
        </p>
      </div>

      {/* L25 — 시행 예정 개정(수신 범위: 운영사만 · 사용자 결정 2026-10-28). 목록 조회와 독립이다 */}
      <UpcomingAmendmentsPanel />

      {list.isLoading ? (
        <div className="py-12 text-center text-gray-500">불러오는 중...</div>
      ) : list.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          카탈로그를 불러오지 못했습니다. {extractApiError(list.error).message}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            <span data-testid="catalog-summary" className="text-gray-700">
              활성 {activeRows.length}개 중 미기재 칸 있음 <strong>{missingCount}</strong>개 · 주기가
              비어 이행 항목이 안 만들어지는 의무 <strong className="text-rose-700">{blockedCount}</strong>개
            </span>
            <label className="ml-auto inline-flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
              />
              미기재 칸 있는 것만
            </label>
          </div>

          {visibleRows.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              {onlyMissing ? '미기재 칸이 있는 의무가 없습니다.' : '카탈로그 행이 없습니다.'}
            </div>
          ) : (
            <Card padding="none" className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CELL_CLASS}>문서명</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>주기</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>기간 기준</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>기한 기준</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>담당 주체</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>대행 분야</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>활성</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>버전</th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {visibleRows.map((row) => (
                    <CatalogRow
                      key={row.id}
                      row={row}
                      onEdit={() => setEditing(row)}
                      onHistory={() => setHistoryOf(row)}
                      onTemplates={() => setTemplatesOf(row)}
                      onLines={() => setLinesOfId(row.id)}
                    />
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {editing && (
        <CatalogEditModal
          // remount로 폼 초기값을 그 행에서 다시 읽는다(선례 `Announcements`의 같은 이유)
          key={editing.id}
          row={editing}
          rows={rows}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh(editing.id);
          }}
          onVerified={() => refresh(editing.id)}
        />
      )}

      {linesOf && <CatalogConditionsModal row={linesOf} onClose={() => setLinesOfId(null)} onChanged={() => refresh(linesOf.id)} />}

      {historyOf && <CatalogHistoryModal row={historyOf} onClose={() => setHistoryOf(null)} />}

      {templatesOf && (
        <ObligationTemplateLinksModal obligation={templatesOf} onClose={() => setTemplatesOf(null)} />
      )}
    </div>
  );
}

/**
 * 비어 있으면 「미기재」를 흐리게 — 빈 칸과 값 「미정」(`UNDETERMINED`)을 같은 말로 읽지 않게 한다.
 * (종전 `emptyLabel` 「미검토」는 대행 분야 칸이 NOT NULL이 되며 걷혔다 — 2026-11-03)
 */
function ValueCell({ label }: { label: string | null }) {
  return (
    <td className="px-4 py-3 text-sm whitespace-nowrap">
      {label === null ? <span className="text-ink-400">미기재</span> : label}
    </td>
  );
}

function CatalogRow({
  row,
  onEdit,
  onHistory,
  onTemplates,
  onLines,
}: {
  row: AdminLegalObligationRow;
  onEdit: () => void;
  onHistory: () => void;
  onTemplates: () => void;
  onLines: () => void;
}) {
  const missing = missingCatalogFields(row);
  // web 법정 서류 화면과 같은 한 자리 — 사본을 두지 않는다(그 함수 docblock)
  const recurrenceLabel = formatObligationRecurrence(row);

  return (
    <tr data-testid={`catalog-row-${row.key}`} className={row.isActive ? '' : 'bg-surface-soft'}>
      <td className="px-4 py-3 text-sm">
        <div className="font-semibold text-ink-900">{row.title}</div>
        <div className="mt-0.5 text-xs text-ink-500">{row.statuteCitation}</div>
      </td>
      <ValueCell label={recurrenceLabel} />
      <ValueCell
        label={row.periodBasis === null ? null : LEGAL_OBLIGATION_PERIOD_BASIS_LABEL[row.periodBasis]}
      />
      <ValueCell
        label={
          row.deadlineBasis === null ? null : LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL[row.deadlineBasis]
        }
      />
      <ValueCell
        label={
          row.responsibleParty === null
            ? null
            : LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL[row.responsibleParty]
        }
      />
      <ValueCell label={LEGAL_OBLIGATION_AGENCY_FIELD_LABEL[row.agencyField]} />
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        {row.isActive ? '활성' : <span className="text-ink-500">비활성</span>}
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="flex flex-wrap gap-1">
          {blocksMaterialization(row) && (
            <Badge tone="rose" size="sm">
              이행 항목 안 만들어짐
            </Badge>
          )}
          {missing.length > 0 ? (
            <Badge tone="amber" size="sm">
              <span title={missing.map((f) => CATALOG_MISSING_FIELD_LABEL[f]).join(', ')}>
                미기재 칸 있음 ({missing.length})
              </span>
            </Badge>
          ) : (
            <Badge tone="emerald" size="sm">
              기재 완료
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">v{row.version}</td>
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        <div className="inline-flex gap-2">
          <Button variant="secondary" size="sm" onClick={onHistory}>
            이력 {row.historyCount}
          </Button>
          <Button variant="secondary" size="sm" onClick={onTemplates}>
            양식
          </Button>
          <Button variant="secondary" size="sm" onClick={onLines}>
            조건·변형 {row.conditions.length + row.intervalVariants.length}
          </Button>
          <Button variant="primary" size="sm" onClick={onEdit}>
            편집
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── 편집 ────────────────────────────────────────────────────────────────────

function CatalogEditModal({
  row,
  rows,
  onClose,
  onSaved,
  onVerified,
}: {
  row: AdminLegalObligationRow;
  /** 카탈로그 전체 — 사건 공유 원천 후보와 그 변형을 고르는 재료 */
  rows: readonly AdminLegalObligationRow[];
  onClose: () => void;
  onSaved: () => void;
  onVerified: () => void;
}) {
  const { showToast } = useToastStore();
  const [verifiedAt, setVerifiedAt] = useState<string | null>(row.lastVerifiedAt);
  const verify = useMutation({
    mutationFn: () => legalObligationCatalogApi.verifyObligation(row.id),
    onSuccess: (result) => {
      setVerifiedAt(result.verifiedAt);
      showToast('조문과 대조 완료로 기록했습니다(개정 아님 — 버전 그대로).', 'success');
      onVerified();
    },
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });
  const initial = useMemo(() => rowToForm(row), [row]);
  const [form, setForm] = useState<CatalogFormState>(initial);
  const body = buildUpdateBody(row, form);
  const isDirty = body !== null || form.changeReason.trim() !== '';
  const blockedReason = formBlockedReason(form) ?? (body === null ? '바뀐 칸이 없습니다.' : undefined);

  const save = useMutation({
    mutationFn: () => {
      // blockedReason이 저장 버튼을 막으므로 여기 오는 body는 null이 아니다
      if (body === null) throw new Error('바뀐 칸이 없습니다.');
      return legalObligationCatalogApi.update(row.id, body);
    },
    onSuccess: (result) => {
      showToast(
        result.changedFields.length === 0
          ? '바뀐 칸이 없어 개정하지 않았습니다.'
          : `개정했습니다 — 이력 1건이 남고 v${row.version + 1}이 되었습니다.`,
        'success',
      );
      onSaved();
    },
    // ⚠️ 모달을 닫지 않는다 — 고친 입력을 잃지 않게(409 동시 개정이면 운영자가 다시 불러와야 한다)
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const set = <K extends keyof CatalogFormState>(key: K, value: CatalogFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={`법정 의무 편집 — ${row.title}`}
      size="xl"
      isDirty={isDirty}
      closeLocked={save.isPending}
      footer={
        <EditFooter
          saving={save.isPending}
          blockedReason={blockedReason}
          onClose={onClose}
          onSave={() => save.mutate()}
        />
      }
    >
      <div className="space-y-5">
        <div
          data-testid="revision-notice"
          className="rounded-card border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          저장하면 <strong>개정 이력</strong>이 남고 버전이 v{row.version}에서 v{row.version + 1}로
          오릅니다. 이미 만들어진 현장의 이행 항목은 그때의 기준을 그대로 유지합니다.
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
          <span>
            식별자 <code>{row.key}</code> · 표시 순서 {row.displayOrder} · 문자 알림{' '}
            {row.smsEscalation ? '포함' : '미포함'}
            <span className="ml-1">(이 셋은 이 화면에서 바꾸지 않습니다)</span>
          </span>
          <span data-testid="obligation-verified">
            · 조문 대조 {verifiedAt ? formatDateTimeISO(new Date(verifiedAt)).slice(0, 10) : '안 됨'}
          </span>
          {/* 저장과 별개의 행위 — 누르는 즉시 서버 시각으로 기록된다(개정 아님 · 폼의 dirty와 무관) */}
          <Button variant="secondary" size="sm" disabled={verify.isPending} onClick={() => verify.mutate()}>
            조문과 대조 완료
          </Button>
        </div>

        <Field label="문서명">
          <input
            type="text"
            value={form.title}
            maxLength={300}
            onChange={(e) => set('title', e.target.value)}
            className={FORM_CONTROL_CLASS}
          />
        </Field>

        <Field label="설명">
          <textarea
            value={form.description}
            maxLength={5000}
            onChange={(e) => set('description', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="주기 종류" hint="비우면 현장에 이행 항목이 만들어지지 않습니다. 「상시」는 검토했더니 주기가 없는 상태 의무입니다.">
            <select
              value={form.recurrence}
              onChange={(e) => set('recurrence', e.target.value as CatalogFormState['recurrence'])}
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {OBLIGATION_RECURRENCE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {LEGAL_OBLIGATION_RECURRENCE_LABEL[value]}
                </option>
              ))}
            </select>
          </Field>

          <RecurrenceRuleFields form={form} set={set} />

          <Field label="기간 기준" hint="비우면 연도로 계산합니다. 건설 현장은 공사 기간을 고르세요.">
            <select
              value={form.periodBasis}
              onChange={(e) => set('periodBasis', e.target.value as CatalogFormState['periodBasis'])}
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_PERIOD_BASIS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="기한 기준" hint="비우면 기간이 끝나는 날을 기한으로 봅니다.">
            <select
              value={form.deadlineBasis}
              onChange={(e) =>
                set('deadlineBasis', e.target.value as CatalogFormState['deadlineBasis'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="담당 주체">
            <select
              value={form.responsibleParty}
              onChange={(e) =>
                set('responsibleParty', e.target.value as CatalogFormState['responsibleParty'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="대행 분야"
            hint="어느 분야의 대행 기관이 이 서류를 작성하는지 고릅니다. 분야를 가리지 않는다면 「어느 분야든」을 고르세요."
          >
            <select
              value={form.agencyField}
              onChange={(e) => set('agencyField', e.target.value as CatalogFormState['agencyField'])}
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_AGENCY_FIELD_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="적용 범위" hint="「전 사업장」은 조문을 읽어 확인한 뒤에만 고르세요.">
            <select
              value={form.applicabilityScope}
              onChange={(e) =>
                set('applicabilityScope', e.target.value as CatalogFormState['applicabilityScope'])
              }
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_APPLICABILITY_SCOPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="증빙 출처">
            <select
              value={form.evidenceSource}
              onChange={(e) =>
                set('evidenceSource', e.target.value as CatalogFormState['evidenceSource'])
              }
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_EVIDENCE_SOURCE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="법정 선임 직위" hint="직위 하나에 의무 하나만 둘 수 있습니다.">
            <select
              value={form.legalPosition}
              onChange={(e) =>
                set('legalPosition', e.target.value as CatalogFormState['legalPosition'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">없음</option>
              {LEGAL_POSITION_ORDER.map((key) => (
                <option key={key} value={key}>
                  {LEGAL_POSITIONS[key].label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="근거 법령 인용" hint="원문 표기 그대로 적습니다(오기를 고쳐 적지 않습니다).">
            <input
              type="text"
              value={form.statuteCitation}
              onChange={(e) => set('statuteCitation', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          <Field label="법령명">
            <input
              type="text"
              value={form.statuteLawName}
              maxLength={300}
              onChange={(e) => set('statuteLawName', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          <Field label="조·항·호">
            <input
              type="text"
              value={form.statuteArticleRef}
              maxLength={200}
              onChange={(e) => set('statuteArticleRef', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          {/* 체크박스는 자기 label을 가지므로 Field(<label>)로 감싸지 않는다 — label 중첩은 무효다 */}
          <div>
            <span className={FORM_LABEL_CLASS}>활성</span>
            <label className="inline-flex h-[42px] items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              활성 (끄면 새 이행 항목이 만들어지지 않습니다)
            </label>
          </div>
        </div>

        <Field label="출처 메모" hint="이 값을 어디서 확인했는지 적어 두세요.">
          <textarea
            value={form.sourceNote}
            maxLength={5000}
            onChange={(e) => set('sourceNote', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>

        <SubjectAndEventSourceFields row={row} rows={rows} form={form} set={set} />

        <p data-testid="lines-pointer" className="text-xs text-ink-500">
          적용 조건 {row.conditions.length}줄 · 간격 변형 {row.intervalVariants.length}줄 — 목록의 「조건·변형」에서 줄마다 고칩니다(줄 저장마다 따로 기록됩니다).
        </p>

        <Field label="개정 사유" hint="이력에 함께 남습니다(법 개정 · 오기 정정 · 조문 검증 결과 등).">
          <textarea
            value={form.changeReason}
            maxLength={2000}
            onChange={(e) => set('changeReason', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className={FORM_LABEL_CLASS}>{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

/**
 * 주기 규칙 칸(②-b · D1 ⓑ) — 종류에 따라 보이는 칸이 다르다: 「주기적」이면 반복 간격·기산(+ 「기준일부터」면 최초 간격·허용 창) ·
 * 「사유 발생 시」면 사건 기한 오프셋(D5). 조합의 모순은 `formBlockedReason`이 저장 전에 잡고(서버가 같은 함수로 한 번 더) 미리보기
 * 문구는 web과 같은 `formatObligationRecurrence`다.
 */
function RecurrenceRuleFields({
  form,
  set,
}: {
  form: CatalogFormState;
  set: <K extends keyof CatalogFormState>(key: K, value: CatalogFormState[K]) => void;
}) {
  const rule = formToRule(form);
  const unitOptions = (
    <>
      <option value="">—</option>
      {(Object.keys(LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL) as Array<keyof typeof LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL>).map((u) => (
        <option key={u} value={u}>
          {LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL[u]}
        </option>
      ))}
    </>
  );
  if (form.recurrence === 'PERIODIC') {
    return (
      <>
        <Field label="반복 간격" hint={`미리보기: ${formatObligationRecurrence({ recurrence: 'PERIODIC', recurrenceRule: rule }) ?? ''}`}>
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              aria-label="반복 간격 수"
              value={form.intervalCount}
              onChange={(e) => set('intervalCount', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
            <select
              aria-label="반복 간격 단위"
              value={form.intervalUnit}
              onChange={(e) => set('intervalUnit', e.target.value as CatalogFormState['intervalUnit'])}
              className={FORM_CONTROL_CLASS}
            >
              {unitOptions}
            </select>
          </div>
        </Field>
        <Field label="기산" hint="달력 기준 = 달·분기·반기·연도 묶음(일·주 간격은 월 묶음) · 기준일부터 = 마지막 실시일(또는 설치·배치일)부터 간격.">
          <select
            value={form.intervalAnchor}
            onChange={(e) => set('intervalAnchor', e.target.value as CatalogFormState['intervalAnchor'])}
            className={FORM_CONTROL_CLASS}
          >
            <option value="">—</option>
            {(Object.keys(LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL) as Array<keyof typeof LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL>).map((a) => (
              <option key={a} value={a}>
                {LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL[a]}
              </option>
            ))}
          </select>
        </Field>
        {form.intervalAnchor === 'ROLLING' && (
          <>
            <Field label="최초 간격" hint="설치·신설·배치일부터 첫 기한까지(비우면 반복 간격과 같다). 안전검사 「최초 3년」·근골격계 「신설 1년」.">
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  aria-label="최초 간격 수"
                  value={form.initialIntervalCount}
                  onChange={(e) => set('initialIntervalCount', e.target.value)}
                  className={FORM_CONTROL_CLASS}
                />
                <select
                  aria-label="최초 간격 단위"
                  value={form.initialIntervalUnit}
                  onChange={(e) => set('initialIntervalUnit', e.target.value as CatalogFormState['initialIntervalUnit'])}
                  className={FORM_CONTROL_CLASS}
                >
                  {unitOptions}
                </select>
              </div>
            </Field>
            <Field label="허용 창(개월)" hint="「매 N년이 되는 날을 기준으로 전후 M개월」의 M. 비우면 기한 당일까지.">
              <input
                type="number"
                min={1}
                value={form.cycleWindowMonths}
                onChange={(e) => set('cycleWindowMonths', e.target.value)}
                className={FORM_CONTROL_CLASS}
              />
            </Field>
          </>
        )}
      </>
    );
  }
  if (form.recurrence === 'ON_EVENT') {
    return (
      <Field label="사건 기한" hint="사건일부터 법정 기한까지(0 = 사건 당일). 비우면 고정 기한 없음 — 현장이 사건마다 기한을 직접 넣습니다.">
        <div className="flex gap-2">
          <input
            type="number"
            min={0}
            aria-label="사건 기한 수"
            value={form.eventDeadlineCount}
            onChange={(e) => set('eventDeadlineCount', e.target.value)}
            className={FORM_CONTROL_CLASS}
          />
          <select
            aria-label="사건 기한 단위"
            value={form.eventDeadlineUnit}
            onChange={(e) => set('eventDeadlineUnit', e.target.value as CatalogFormState['eventDeadlineUnit'])}
            className={FORM_CONTROL_CLASS}
          >
            {unitOptions}
          </select>
        </div>
      </Field>
    );
  }
  return null;
}

/**
 * 대상 · 사건 공유 원천 넷(W1 PATCH 칸 · W3 2026-11-04 화면). 서버 저장 뒤 형상(`legal-obligation-catalog-shape.ts`)이 정본이고, 여기서는
 * 배타·원천 선택지를 «고를 수 있는 것만» 보이게 한다:
 *   · 대상 = 라디오 셋(현장 단위 · 대상 직위 · 공정·유해인자) — 한쪽을 고르면 다른 쪽을 비운다(`SUBJECT_EXCLUSIVE`가 폼에서 생기지 않게)
 *   · 원천 의무 = `eventSourceCandidates`(ON_EVENT · 활성 · 원천 없음 · 자기 제외 — 지금 값은 후보가 아니어도 남긴다)
 *   · 원천 변형 = 고른 원천의 `EVENT_CHOICE` 변형 체크박스(원천을 바꾸면 비운다 — 다른 의무의 변형 id는 의미가 없다)
 */
function SubjectAndEventSourceFields({
  row,
  rows,
  form,
  set,
}: {
  row: AdminLegalObligationRow;
  rows: readonly AdminLegalObligationRow[];
  form: CatalogFormState;
  set: <K extends keyof CatalogFormState>(key: K, value: CatalogFormState[K]) => void;
}) {
  const mode = subjectModeOf(form);
  const candidates = eventSourceCandidates(rows, row, form.eventSourceObligationKey);
  // 기준일 선행 의무 후보(2026-11-04 — 보수교육 ← 신규교육): 사람 대상 · 활성 · 선행 없음 · 자기 제외
  const anchorCandidates = anchorSourceCandidates(rows, row, form.anchorSourceObligationKey);
  const source = rows.find((r) => r.key === form.eventSourceObligationKey);
  const choices = eventChoiceVariantsOf(source);
  const pickMode = (next: 'SITE' | 'POSITIONS' | 'PROCESS_HAZARDS') => {
    if (next !== 'POSITIONS') set('subjectLegalPositions', []);
    set('subjectProcessHazards', next === 'PROCESS_HAZARDS');
  };
  const togglePosition = (key: LegalPositionKey, on: boolean) =>
    // 순서를 보존한다 — 새 직위는 끝에(서버 비교가 순서를 본다 · 정렬하면 손대지 않은 순서가 개정으로 실린다)
    set('subjectLegalPositions', on ? [...form.subjectLegalPositions, key] : form.subjectLegalPositions.filter((k) => k !== key));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <fieldset data-testid="subject-fields">
        <legend className={FORM_LABEL_CLASS}>대상</legend>
        {mode === 'BOTH' && (
          <p className="mb-1 text-xs text-rose-700">대상 직위와 공정·유해인자 대상이 함께 지정돼 있습니다 — 한쪽을 골라 주세요.</p>
        )}
        <div className="space-y-1 text-sm text-ink-700">
          <label className="flex items-center gap-2">
            <input type="radio" name="subject-mode" checked={mode === 'SITE'} onChange={() => pickMode('SITE')} />
            현장 단위(대상 없음)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="subject-mode"
              checked={mode === 'POSITIONS'}
              onChange={() => {
                set('subjectProcessHazards', false);
                if (form.subjectLegalPositions.length === 0) set('subjectLegalPositions', [LEGAL_POSITION_ORDER[0]]);
              }}
            />
            사람 대상 — 대상 직위
          </label>
          {(mode === 'POSITIONS' || mode === 'BOTH') && (
            <div className="ml-6 grid grid-cols-1 gap-1">
              {LEGAL_POSITION_ORDER.map((key) => (
                <label key={key} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={form.subjectLegalPositions.includes(key)}
                    onChange={(e) => togglePosition(key, e.target.checked)}
                  />
                  {LEGAL_POSITIONS[key].label}
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2">
            <input type="radio" name="subject-mode" checked={mode === 'PROCESS_HAZARDS'} onChange={() => pickMode('PROCESS_HAZARDS')} />
            공정 × 유해인자 대상
          </label>
        </div>
      </fieldset>

      <Field
        label="기준일 선행 의무"
        hint="기준일부터 도는 사람 대상 의무의 첫 주기 기산일을 같은 사람의 다른 의무 이수일(또는 면제 성립일)에서 가져올 때 고릅니다(보수교육 ← 신규교육 · 시행규칙 제29조① · 선행은 한 단계). 그 기록이 없는 사람의 줄은 날짜 없이 「신규교육 이수 후 정해집니다」로 보입니다."
      >
        <select
          data-testid="anchor-source-select"
          value={form.anchorSourceObligationKey}
          onChange={(e) => set('anchorSourceObligationKey', e.target.value)}
          className={FORM_CONTROL_CLASS}
        >
          <option value="">없음(사람이면 직위 배정일부터)</option>
          {anchorCandidates.map((r) => (
            <option key={r.key} value={r.key}>
              {r.title}
            </option>
          ))}
        </select>
      </Field>

      <div>
        <Field
          label="사건 공유 원천 의무"
          hint="이 의무가 다른 의무의 사건을 따라 만들어질 때 고릅니다(주기가 「사유 발생 시」일 때만 · 공유는 한 단계)."
        >
          <select
            value={form.eventSourceObligationKey}
            onChange={(e) => {
              set('eventSourceObligationKey', e.target.value);
              set('eventSourceVariantIds', []);
            }}
            className={FORM_CONTROL_CLASS}
          >
            <option value="">없음(자기 사건)</option>
            {candidates.map((r) => (
              <option key={r.key} value={r.key}>
                {r.title}
              </option>
            ))}
          </select>
        </Field>
        {form.eventSourceObligationKey !== '' && (
          <fieldset data-testid="event-source-variants" className="mt-2">
            <legend className={FORM_LABEL_CLASS}>원천 변형(비우면 원천의 모든 사건)</legend>
            {choices.length === 0 ? (
              <p className="text-xs text-ink-500">원천 의무에 「사건 등록 시 선택」 변형이 없습니다.</p>
            ) : (
              choices.map((v) => (
                <label key={v.id} className="flex items-center gap-2 text-xs text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.eventSourceVariantIds.includes(v.id)}
                    onChange={(e) =>
                      set(
                        'eventSourceVariantIds',
                        e.target.checked ? [...form.eventSourceVariantIds, v.id] : form.eventSourceVariantIds.filter((id) => id !== v.id),
                      )
                    }
                  />
                  {v.label ?? v.id} ({formatObligationInterval(v.interval)})
                </label>
              ))
            )}
          </fieldset>
        )}
      </div>
    </div>
  );
}

/**
 * 푸터는 별도 컴포넌트여야 한다 — `useModalRequestClose`는 `Modal` children 하위에서만 값을 잡는다
 * (선례 `Announcements`의 `FormFooter`). 취소는 `requestClose`를 거쳐 dirty 확인을 받는다.
 */
function EditFooter({
  saving,
  blockedReason,
  onClose,
  onSave,
}: {
  saving: boolean;
  /** 저장을 막는 사유 — 있으면 곧 비활성이다(별 플래그를 두지 않는다 · 선례 `FormFooter`) */
  blockedReason?: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => (requestClose ?? onClose)()}
        disabled={saving}
      >
        취소
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={onSave}
        disabled={saving || blockedReason !== undefined}
        title={blockedReason}
      >
        {saving ? '저장 중...' : '저장 (개정 이력 남김)'}
      </Button>
    </>
  );
}

// ── 이력 ────────────────────────────────────────────────────────────────────

function CatalogHistoryModal({
  row,
  onClose,
}: {
  row: AdminLegalObligationRow;
  onClose: () => void;
}) {
  // W3(2026-11-04) — 종전 `/history`(개정 행만)에서 `/timeline`(개정 + 근거 변경 · 대조 완료 — 시간순 한 줄기)으로 옮겼다(사용자 결정 2026-11-03 Q2)
  const timeline = useQuery({
    queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.timeline(row.id),
    queryFn: () => legalObligationCatalogApi.timeline(row.id),
  });
  const entries = timeline.data?.entries ?? [];

  return (
    <Modal open onClose={onClose} title={`이력 — ${row.title}`} size="xl">
      <p className="mb-3 text-sm text-ink-600">
        현재 v{row.version}. <strong>개정</strong> 행의 버전은 그 개정 «전» 내용의 버전입니다. <strong>근거 변경</strong>·
        <strong>대조 완료</strong>는 버전을 올리지 않는 별 기록입니다.
      </p>
      {timeline.isLoading ? (
        <div className="py-6 text-center text-gray-500">불러오는 중...</div>
      ) : timeline.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          이력을 불러오지 못했습니다. {extractApiError(timeline.error).message}
        </div>
      ) : entries.length === 0 ? (
        <div className="py-6 text-center text-gray-500">아직 기록이 없습니다.</div>
      ) : (
        <ol className="space-y-3">
          {entries.map((e) => (
            <li key={e.kind === 'REVISION' ? e.historyId : e.auditId} data-testid={`timeline-${e.kind}`} className="rounded-card border border-line p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 font-semibold text-ink-900">
                  <Badge tone={TIMELINE_KIND_TONE[e.kind]} size="sm">
                    {TIMELINE_KIND_LABEL[e.kind]}
                  </Badge>
                  {e.kind === 'REVISION' && `v${e.version} → v${e.version + 1}`}
                </span>
                <span className="text-xs text-ink-500">{formatDateTimeISO(new Date(e.at)).replace('T', ' ')}</span>
              </div>
              <div className="mt-1 text-ink-700">{describeTimelineEntry(e)}</div>
              {e.kind === 'REVISION' && e.changeReason && <div className="mt-1 text-ink-600">사유: {e.changeReason}</div>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

const TIMELINE_KIND_LABEL: Record<AdminLegalObligationTimelineEntry['kind'], string> = {
  REVISION: '개정',
  CONDITION_SOURCE_CHANGED: '근거 변경',
  OBLIGATION_VERIFIED: '조문 대조 완료',
  CONDITION_VERIFIED: '별표 대조 완료',
};

const TIMELINE_KIND_TONE: Record<AdminLegalObligationTimelineEntry['kind'], 'amber' | 'blue' | 'emerald'> = {
  REVISION: 'amber',
  CONDITION_SOURCE_CHANGED: 'blue',
  OBLIGATION_VERIFIED: 'emerald',
  CONDITION_VERIFIED: 'emerald',
};

function describeSourceSnapshot(s: AdminLegalObligationConditionSourceSnapshot | null): string {
  if (s === null) return '없음';
  const kind = LEGAL_OBLIGATION_CONDITION_SOURCE_KIND_LABEL[s.sourceKind];
  if (s.sourceKind !== 'LAW_ANNEX' && s.sourceKind !== 'ADMIN_RULE_ANNEX') return kind;
  const doc = s.sourceKind === 'LAW_ANNEX' ? `법령ID ${s.sourceLawId}` : `행정규칙ID ${s.sourceAdminRuleId}`;
  return `${kind} ${doc} ${s.sourceAnnexNumber ? formatAnnexLabel('별표', s.sourceAnnexNumber, s.sourceAnnexSubNumber) : ''}`.trim();
}

function describeTimelineEntry(e: AdminLegalObligationTimelineEntry): string {
  switch (e.kind) {
    case 'REVISION':
      return `바뀐 칸: ${e.changedFields.map(revisionFieldLabel).join(', ') || '없음'}`;
    case 'CONDITION_SOURCE_CHANGED':
      return `${LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[e.axis]} 조건의 근거: ${describeSourceSnapshot(e.before)} → ${describeSourceSnapshot(e.after)}`;
    case 'OBLIGATION_VERIFIED':
      return '조문과 대조했습니다.';
    case 'CONDITION_VERIFIED':
      return `${LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[e.axis]} 조건을 별표와 대조했습니다.`;
  }
}
